import { useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";

import { useToast } from "hooks/toast";

import { useAppSelector } from "store/rootReducer";

import { AdminArea } from "components/Body/AdminArea";
import Button from "components/Button";
import { Drawer } from "components/Drawer";
import { DropdownGlobal } from "components/DropdownGlobal";
import ModalDefault from "components/ModalDefault";

import {
  AdGroupMappingService,
  type AdGroupMappingDto,
  type AzureGroupDto,
} from "services/adGroupMapping";
import { AttendantRoleService } from "services/attendantRole";

import { FORMS_ADMIN } from "utils/entities/permission";

import {
  CardActions,
  ContainerContent,
  EmptyState,
  IconButton,
  MappingCard,
  MappingCardCollapsed,
  MappingCardField,
  MappingCardSummary,
  MappingIndex,
} from "./styles";

const FIELD_HEIGHT = "48px";

interface DraftMapping {
  localId: string;
  id?: number;
  idMapping: string;
  adGroupName: string;
  kentaRoleId: number;
  kentaRoleName: string;
  selectedType?: number;
  defaultPlaceId?: number;
  unitLabel?: string;
  courtLabel?: string;
  courtId?: number;
}

const createLocalId = () =>
  `map-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const RoleMapAd = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { user } = useAppSelector((state) => state.user);

  const customerId = Number(user?.customerId ?? 0);

  // List state
  const [mappings, setMappings] = useState<AdGroupMappingDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Drawer state
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // Draft mappings inside drawer (multiple)
  const [drafts, setDrafts] = useState<DraftMapping[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  // Dropdown data
  const [azureGroups, setAzureGroups] = useState<AzureGroupDto[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]);

  // Place data per expanded card
  const [placeOptions, setPlaceOptions] = useState<any[]>([]);
  const [listProfiles, setListProfiles] = useState<any[]>([]);

  // Load mappings
  const loadMappings = async () => {
    if (!customerId) return;
    setLoading(true);
    const response = await AdGroupMappingService.getAllByCustomerId(customerId);
    if (response.status && Array.isArray(response.data)) {
      setMappings(response.data);
    }
    setLoading(false);
  };

  // Load Azure groups
  const loadAzureGroups = async () => {
    if (!customerId) return;
    const response = await AdGroupMappingService.getAzureGroups(customerId);
    if (response.status && Array.isArray(response.data)) {
      setAzureGroups(response.data);
    }
  };

  // Load profiles
  const loadProfiles = async () => {
    if (!customerId) return;
    const { data } = await AttendantRoleService.getAllPlace(customerId);
    const options = data?.data?.filter((item: any) => item.active) ?? [];
    setProfiles(options);
  };

  // Load places based on profile type
  const loadPlaces = async (type: number) => {
    if (!customerId || type === undefined) return;
    const { body } = await AttendantRoleService.getAllByCustomerId2(
      customerId,
      undefined,
      type,
    );
    if (!body) {
      setPlaceOptions([]);
      setListProfiles([]);
      return;
    }
    setPlaceOptions(type === 1 ? body : (body[0]?.districts ?? []));
    setListProfiles(body ?? []);
  };

  useEffect(() => {
    loadMappings();
    loadAzureGroups();
    loadProfiles();
  }, [customerId]);

  // Track selectedType of expanded card separately to avoid unstable deps
  const expandedDraftType = drafts.find(
    (d) => d.localId === expandedId,
  )?.selectedType;

  useEffect(() => {
    if (expandedDraftType !== undefined) {
      loadPlaces(expandedDraftType);
    } else {
      setPlaceOptions([]);
      setListProfiles([]);
    }
  }, [expandedId, expandedDraftType]);

  // Resolve unit/court labels when placeOptions load for edit mode
  useEffect(() => {
    if (!isEdit || !placeOptions.length || !expandedId) return;

    setDrafts((prev) =>
      prev.map((draft) => {
        if (draft.localId !== expandedId) return draft;
        if (!draft.defaultPlaceId || draft.unitLabel) return draft;

        if (draft.selectedType === 1) {
          const unit = placeOptions.find(
            (p: any) => p.id === draft.defaultPlaceId,
          );
          return unit ? { ...draft, unitLabel: unit.name } : draft;
        }

        for (const district of placeOptions) {
          if (district.id === draft.defaultPlaceId) {
            return { ...draft, unitLabel: district.name };
          }
          const court = district.courts?.find(
            (c: any) => c.id === draft.defaultPlaceId,
          );
          if (court) {
            return {
              ...draft,
              unitLabel: district.name,
              courtLabel: court.name,
              courtId: court.id,
            };
          }
        }
        return draft;
      }),
    );
  }, [placeOptions, isEdit, expandedId]);

  // Filter list
  const filteredMappings = mappings.filter((item) =>
    item.adGroupName?.toLowerCase().includes(search.toLowerCase()),
  );

  // Format for AdminArea
  const formattedList = filteredMappings.map((item) => {
    const profile = profiles.find((p: any) => p.id === item.kentaRoleId);
    return {
      id: item.id,
      name: `${profile?.name ?? `Role ${item.kentaRoleId}`} → ${item.adGroupName}`,
      active: true,
      raw: item,
    };
  });

  // Close drawer
  const closeDrawer = () => {
    setOpenDrawer(false);
    setIsEdit(false);
    setDrafts([]);
    setExpandedId(null);
    setPendingId(null);
  };

  // Open drawer for add (empty, ready for multiple)
  const handleAdd = () => {
    closeDrawer();
    setDrafts([]);
    setOpenDrawer(true);
  };

  // Open drawer for edit (single item)
  const handleEdit = (item: any) => {
    const raw = item.raw as AdGroupMappingDto;
    const profile = profiles.find((p: any) => p.id === raw.kentaRoleId);
    const localId = createLocalId();

    const draft: DraftMapping = {
      localId,
      id: raw.id,
      idMapping: raw.idMapping ?? "",
      adGroupName: raw.adGroupName,
      kentaRoleId: raw.kentaRoleId,
      kentaRoleName: profile?.name ?? "",
      selectedType: profile?.type,
      defaultPlaceId: raw.defaultPlaceId,
    };

    setDrafts([draft]);
    setExpandedId(localId);
    setIsEdit(true);
    setOpenDrawer(true);

    if (profile?.type !== undefined) {
      loadPlaces(profile.type);
    }
  };

  // Add new draft card
  const handleAddDraft = () => {
    const localId = createLocalId();
    setDrafts((prev) => [
      ...prev,
      {
        localId,
        idMapping: "",
        adGroupName: "",
        kentaRoleId: 0,
        kentaRoleName: "",
      },
    ]);
    setExpandedId(localId);
  };

  // Update draft field
  const updateDraft = (localId: string, updates: Partial<DraftMapping>) => {
    setDrafts((prev) =>
      prev.map((d) => (d.localId === localId ? { ...d, ...updates } : d)),
    );
  };

  // Remove draft
  const handleRemoveDraft = async (localId: string) => {
    const draft = drafts.find((d) => d.localId === localId);
    if (!draft) return;

    // If it has a backend ID, delete from API
    if (draft.id) {
      setPendingId(localId);
      const response = await AdGroupMappingService.remove(draft.id);
      if (!response.status) {
        addToast({
          type: "error",
          title: t("common.error"),
          description: response.message || t("common.error"),
        });
        setPendingId(null);
        return;
      }
      await loadMappings();
      setPendingId(null);
    }

    setDrafts((prev) => prev.filter((d) => d.localId !== localId));
    if (expandedId === localId) setExpandedId(null);
  };

  // Save all drafts (Aplicar)
  const handleApply = async () => {
    for (const draft of drafts) {
      if (!draft.adGroupName.trim() || !draft.kentaRoleId) continue;

      const finalPlaceId =
        draft.selectedType === 0 && draft.courtId
          ? draft.courtId
          : draft.defaultPlaceId;

      if (!finalPlaceId) {
        addToast({
          type: "warning",
          title: t("common.warning"),
          description: `${draft.adGroupName}: ${t("adGroupMapping.validation")}`,
        });
        return;
      }

      setPendingId(draft.localId);

      const payload = {
        CustomerId: customerId,
        IdMapping: draft.idMapping,
        AdGroupName: draft.adGroupName.trim(),
        KentaRoleId: draft.kentaRoleId,
        DefaultPlaceId: finalPlaceId,
      };

      const response = draft.id
        ? await AdGroupMappingService.update({ Id: draft.id, ...payload })
        : await AdGroupMappingService.add(payload);

      if (!response.status) {
        addToast({
          type: "error",
          title: t("common.error"),
          description: response.message || t("common.error"),
        });
        setPendingId(null);
        return;
      }
    }

    addToast({
      type: "success",
      title: t("common.success"),
      description: t("adGroupMapping.save"),
    });

    await loadMappings();
    closeDrawer();
  };

  // Azure group options
  const azureGroupOptions = azureGroups.map((g) => ({
    id: g.groupId,
    name: g.displayName,
  }));

  // Courts for expanded card
  const expandedDraft = drafts.find((d) => d.localId === expandedId);
  const districts = listProfiles?.[0]?.districts ?? [];
  const courtsFromSelectedDistrict = expandedDraft?.unitLabel
    ? (districts.find((d: any) => d.name === expandedDraft.unitLabel)?.courts ??
      [])
    : [];
  const showCourts =
    expandedDraft?.selectedType === 0 &&
    !!expandedDraft?.unitLabel &&
    courtsFromSelectedDistrict.length > 0;

  return (
    <>
      <AdminArea
        loading={loading}
        title={t("adGroupMapping.title")}
        InputSearchLabel={t("common.searchBy")}
        InputSearchOnChange={(text) => setSearch(text)}
        searchText={search}
        list={formattedList}
        headerAddButtonOnClick={handleAdd}
        headerTrashButtonOnClick={() => {}}
        onClick={handleEdit}
        setOpenModal={setOpenDrawer}
        setIsEdit={setIsEdit}
        permissionName={FORMS_ADMIN.ROLE_MAP_AD}
        displayAtivoFilter={false}
      />
      <Drawer
        title={
          isEdit ? t("adGroupMapping.edit") : t("adGroupMapping.addMapping")
        }
        open={openDrawer}
        onClose={() => {
          if (!showDeleteModal) closeDrawer();
        }}
        onApply={handleApply}
        applyLabel={t("common.apply")}
      >
        <ContainerContent>
          {!isEdit && (
            <Button onClick={handleAddDraft} disabled={pendingId !== null}>
              {t("adGroupMapping.addMapping")}
            </Button>
          )}

          {drafts.length === 0 && (
            <EmptyState>{t("adGroupMapping.emptyState")}</EmptyState>
          )}

          {drafts.map((draft, index) => {
            const isExpanded = expandedId === draft.localId;
            const isPending = pendingId === draft.localId;
            return (
              <MappingCard key={draft.localId} collapsed={!isExpanded}>
                {isExpanded ? (
                  <>
                    <CardActions
                      style={{ position: "absolute", top: 8, right: 8 }}
                    >
                      {isEdit && draft.id ? (
                        <IconButton
                          className="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setDeleteTargetId(draft.id!);
                            setShowDeleteModal(true);
                          }}
                          title={t("adGroupMapping.remove")}
                          disabled={isPending}
                        >
                          <DeleteIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      ) : (
                        <IconButton
                          className="danger"
                          onClick={() => void handleRemoveDraft(draft.localId)}
                          title={t("adGroupMapping.remove")}
                          disabled={isPending}
                        >
                          ✕
                        </IconButton>
                      )}
                    </CardActions>

                    <MappingIndex>#{index + 1}</MappingIndex>

                    <MappingCardField>
                      <DropdownGlobal
                        labelName={t("adGroupMapping.groupLabel")}
                        options={azureGroupOptions}
                        wantAnObject
                        error={false}
                        setSelected={(item: any) =>
                          updateDraft(draft.localId, {
                            idMapping: item.id,
                            adGroupName: item.name,
                          })
                        }
                        required
                        editDropdown={draft.adGroupName || undefined}
                        defaultValue={draft.adGroupName || undefined}
                        width="100%"
                        height={FIELD_HEIGHT}
                      />
                    </MappingCardField>

                    <MappingCardField>
                      <DropdownGlobal
                        labelName={t("adGroupMapping.roleLabel")}
                        options={profiles}
                        wantAnObject
                        error={false}
                        setSelected={(item: any) => {
                          updateDraft(draft.localId, {
                            kentaRoleId: item.id,
                            kentaRoleName: item.name,
                            selectedType: item.type,
                            defaultPlaceId: undefined,
                            unitLabel: undefined,
                            courtLabel: undefined,
                            courtId: undefined,
                          });
                        }}
                        required
                        editDropdown={draft.kentaRoleName || undefined}
                        defaultValue={draft.kentaRoleName || undefined}
                        width="100%"
                        height={FIELD_HEIGHT}
                      />
                    </MappingCardField>

                    {draft.selectedType !== undefined && (
                      <MappingCardField>
                        <DropdownGlobal
                          key={`place-${draft.localId}-${draft.unitLabel ?? "empty"}`}
                          labelName={
                            draft.selectedType === 1
                              ? t("units.title")
                              : t("labels.counties")
                          }
                          options={placeOptions}
                          wantAnObject
                          error={false}
                          setSelected={(item: any) => {
                            updateDraft(draft.localId, {
                              unitLabel: item.name,
                              defaultPlaceId: item.id,
                              courtLabel: undefined,
                              courtId: undefined,
                            });
                          }}
                          required={false}
                          editDropdown={draft.unitLabel || undefined}
                          defaultValue={draft.unitLabel || undefined}
                          width="100%"
                          height={FIELD_HEIGHT}
                        />
                      </MappingCardField>
                    )}

                    {isExpanded && showCourts && (
                      <MappingCardField>
                        <DropdownGlobal
                          key={`court-${draft.localId}-${draft.courtLabel ?? "empty"}`}
                          labelName={t("labels.courts")}
                          options={courtsFromSelectedDistrict}
                          wantAnObject
                          error={false}
                          setSelected={(item: any) => {
                            updateDraft(draft.localId, {
                              courtLabel: item.name,
                              courtId: item.id,
                            });
                          }}
                          required
                          editDropdown={draft.courtLabel || undefined}
                          defaultValue={draft.courtLabel || undefined}
                          width="100%"
                          height={FIELD_HEIGHT}
                        />
                      </MappingCardField>
                    )}
                  </>
                ) : (
                  <MappingCardCollapsed
                    onClick={() => setExpandedId(draft.localId)}
                  >
                    <MappingCardSummary>
                      <strong>{draft.adGroupName || "—"}</strong> →{" "}
                      {draft.kentaRoleName || "—"}
                    </MappingCardSummary>
                    <CardActions>
                      <IconButton
                        className="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleRemoveDraft(draft.localId);
                        }}
                        title={t("adGroupMapping.remove")}
                        disabled={isPending}
                      >
                        ✕
                      </IconButton>
                    </CardActions>
                  </MappingCardCollapsed>
                )}
              </MappingCard>
            );
          })}
        </ContainerContent>
      </Drawer>
      <ModalDefault
        openModal={showDeleteModal}
        setOpenModal={setShowDeleteModal}
        title={t("common.delete")}
        bodyMessageTitle={t(
          "adGroupMapping.confirmDelete",
          "Deseja excluir este mapeamento?",
        )}
        bodyMessageSubTitle={t(
          "adGroupMapping.confirmDeleteSub",
          "Esta ação não pode ser desfeita.",
        )}
        textConfirm={t("common.delete").toUpperCase()}
        onClickConfirm={async () => {
          if (deleteTargetId) {
            setShowDeleteModal(false);
            const draftToDelete = drafts.find((d) => d.id === deleteTargetId);
            if (draftToDelete) setPendingId(draftToDelete.localId);
            const response = await AdGroupMappingService.remove(deleteTargetId);
            if (response.status) {
              addToast({
                type: "success",
                title: t("common.success"),
                description: t(
                  "common.deleteSuccess",
                  "Mapeamento excluído com sucesso.",
                ),
              });
              await loadMappings();
              closeDrawer();
            } else {
              addToast({
                type: "error",
                title: t("common.error"),
                description: response.message || t("common.error"),
              });
            }
            setPendingId(null);
            setDeleteTargetId(null);
          }
        }}
        textCancel={t("common.cancel").toUpperCase()}
        onClickCancel={() => {
          setShowDeleteModal(false);
          setDeleteTargetId(null);
        }}
      />
    </>
  );
};

export default RoleMapAd;
